import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: {
      main: '#7F56D9',
      light: '#F9F5FF',
    },
    secondary: {
      main: '#B692F6',
      light: '#FCFAFF',
    },
    success: {
      main: '#079455',
      light:'#ECFDF3',
    },
    error: {
      main: '#F04438',
      light:'#FEF3F2',
    },
    warning: {
      main: '#F79009',
    },
    info: {
      main: '#4069F5',
    },
    background: {
      default: '#FFFFFF',
      paper: '#ffffff'
    },
    text: {
      primary: '#000000',
      secondary: '#667085',
    },
    divider: '#E3E8EB',
    action: {
      disabledBackground: '#E1E1E1',
    },
    custom: {
      border: '#D0D5DD',
      borderLight: '#E4E7EC',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 960,
      xl: 1280,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100%', // Default maximum width for all containers
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: '#B42318',
        },
        root: {
          // color: '#475467', // Change the label color (use your desired color)
          '&.Mui-focused': {
            // color: '#475467', // Change the label color when focused
          },
          '&.MuiInputLabel-shrink': {
            // color: '#475467', // Change the label color when it shrinks (e.g., after focus)
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius:'1vw',
          '&:hover': {
            backgroundColor: '#F9F5FF',
          },
        },
      },},
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #E4E7EC',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '0px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: '8px',
          boxShadow: 'none',
          textTransform: 'none',
          [`@media (max-width:600px)`]: { // xs screen (mobile)
            fontSize: '0.75rem',
          },
          [`@media (min-width:600px) and (max-width:960px)`]: { // sm screen
            fontSize: '0.825rem',
          },
          [`@media (min-width:960px) and (max-width:1280px)`]: { // md screen
            fontSize: '0.875rem',
          },
          [`@media (min-width:1280px) and (max-width:1920px)`]: { // lg screen
            fontSize: '0.925rem',
          },
          [`@media (min-width:1920px)`]: { // xl screen
            fontSize: '1rem',
          },
        },
        outlined: {
          borderColor: '#D0D5DD',
          backgroundColor: '#FFFFFF',
          color: '#000000',
          fontSize: '0.875rem',
          '&:hover': {
            boxShadow: 'none',
            borderColor: '#D0D5DD',
          },
        },
        contained: {
          backgroundColor:"#7F56D9",
          opacity:1,
          boxShadow: 'none',
          fontSize: '0.875rem',
          '&:hover': {
            boxShadow: 'none',            
            backgroundColor:"#7F56D9",
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
 spacing: (factor: number) => `${factor * 0.556}vw`,
  typography: {
  fontFamily: 'Inter, Arial, sans-serif',
  fontSize: 12, // base font size in px
  h1: {
    fontWeight: 'bold',
    fontSize: '2.5rem', // 40px
    // ...
    [`@media (max-width:600px)`]: { fontSize: '1.5rem' }, // 24px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '2rem' }, // 32px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '2.25rem' }, // 36px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '2.5rem' }, // 40px
    [`@media (min-width:1920px)`]: { fontSize: '2.75rem' }, // 44px
  },
  h2: {
    fontWeight: 'bold',
    fontSize: '2rem', // 32px
    [`@media (max-width:600px)`]: { fontSize: '1.25rem' }, // 20px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '1.75rem' }, // 28px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1.875rem' }, // 30px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '2rem' }, // 32px
    [`@media (min-width:1920px)`]: { fontSize: '2.25rem' }, // 36px
  },
  h3: {
    fontWeight: 'bold',
    fontSize: '1.75rem', // 28px
    [`@media (max-width:600px)`]: { fontSize: '1.125rem' }, // 18px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '1.5rem' }, // 24px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1.625rem' }, // 26px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.75rem' }, // 28px
    [`@media (min-width:1920px)`]: { fontSize: '1.875rem' }, // 30px
  },
  h4: {
    fontWeight: '600',
    fontSize: '1.875rem', // 30px
    [`@media (max-width:600px)`]: { fontSize: '1.125rem' }, // 18px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '1.5rem' }, // 24px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1.625rem' }, // 26px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.75rem' }, // 28px
    [`@media (min-width:1920px)`]: { fontSize: '1.875rem' }, // 30px
  },
  h5: {
    fontWeight: '600',
    color: '#000000',
    fontSize: '1.5rem', // 24px
    [`@media (max-width:600px)`]: { fontSize: '1rem' }, // 16px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '1.25rem' }, // 20px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1.375rem' }, // 22px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.5rem' }, // 24px
    [`@media (min-width:1920px)`]: { fontSize: '1.625rem' }, // 26px
  },
  h6: {
    fontWeight: '500',
    color: '#101828',
    fontSize: '1.25rem', // 20px
    [`@media (max-width:600px)`]: { fontSize: '0.875rem' }, // 14px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '1rem' }, // 16px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1.125rem' }, // 18px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.25rem' }, // 20px
    [`@media (min-width:1920px)`]: { fontSize: '1.375rem' }, // 22px
  },
  subtitle1: {
    fontWeight: '400',
    color: '#475467',
    fontSize: '1rem', // 16px
    [`@media (max-width:600px)`]: { fontSize: '0.875rem' }, // 14px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '0.95rem' }, // ~15.2px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1rem' }, // 16px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.05rem' }, // ~16.8px
    [`@media (min-width:1920px)`]: { fontSize: '1.1rem' }, // ~17.6px
  },
  subtitle2: {
    fontWeight: '600',
    fontSize: '1rem', // 16px
    [`@media (max-width:600px)`]: { fontSize: '0.875rem' }, // 14px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '0.95rem' }, // ~15.2px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '1rem' }, // 16px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '1.05rem' }, // ~16.8px
    [`@media (min-width:1920px)`]: { fontSize: '1.1rem' }, // ~17.6px
  },
  body1: {
    fontSize: '0.875rem', // 14px
    fontWeight: '500',
    [`@media (max-width:600px)`]: { fontSize: '0.75rem' }, // 12px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '0.8rem' }, // ~12.8px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '0.85rem' }, // ~13.6px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '0.875rem' }, // 14px
    [`@media (min-width:1920px)`]: { fontSize: '0.95rem' }, // ~15.2px
  },
  body2: {
    fontSize: '0.875rem', // 14px
    color: '#101828',
  },
  caption: {
    fontSize: '0.75rem', // 12px,
    color:'#475467'
  },
  overline: {
    fontSize: '0.75rem', // 12px
    textTransform: 'uppercase',
  },
  button: {
    fontSize: '0.875rem', // 14px
    fontWeight: 'bold',
    [`@media (max-width:600px)`]: { fontSize: '0.75rem' }, // 12px
    [`@media (min-width:600px) and (max-width:960px)`]: { fontSize: '0.8rem' }, // ~12.8px
    [`@media (min-width:960px) and (max-width:1280px)`]: { fontSize: '0.85rem' }, // ~13.6px
    [`@media (min-width:1280px) and (max-width:1920px)`]: { fontSize: '0.875rem' }, // 14px
    [`@media (min-width:1920px)`]: { fontSize: '0.95rem' }, // ~15.2px
  },
  body3: {
    fontSize: '0.75rem', // 12px
    fontWeight: '400',
    color: '#101828',
  },
  subtitle3: {
    fontSize: '0.875rem', // 14px
    fontWeight: '300',
    color: '#475467',
  },
}

});
